import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/i18n";

import { AuthProvider } from "@/contexts/AuthContext";
import { ConsentProvider } from "@/contexts/ConsentContext";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import PublicQuote from "@/pages/PublicQuote";
import PaymentResult from "@/pages/PaymentResult";
import TipPage from "@/pages/TipPage";

// Lazy-loaded routes — split from main bundle for better LCP/TBT
const CityPage = lazy(() => import("@/pages/CityPage"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Contact = lazy(() => import("@/pages/Contact"));
const Tarifs = lazy(() => import("@/pages/Tarifs"));
const MonParcours = lazy(() => import("@/pages/MonParcours"));
const Legal = lazy(() => import("@/pages/Legal"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Login = lazy(() => import("@/pages/Login"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const MonCompte = lazy(() => import("@/pages/MonCompte"));
const Admin = lazy(() => import("@/pages/Admin"));
const Chauffeur = lazy(() => import("@/pages/Chauffeur"));
const Galerie = lazy(() => import("@/pages/Galerie"));
const PartenairesHotels = lazy(() => import("@/pages/PartenairesHotels"));

const RouteFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
  </div>
);

function AppRoutes() {
  const location = useLocation();
  // Handle session_id in URL hash before normal routing
  if (location.hash?.includes("session_id=") && location.pathname !== "/auth/callback") {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/login" element={<Login />} />

        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />

          {/* SEO city pages */}
          <Route path="/vtc-nice" element={<CityPage />} />
          <Route path="/chauffeur-prive-nice" element={<CityPage />} />
          <Route path="/vtc-aeroport-nice" element={<CityPage />} />
          <Route path="/vtc-aeroport-nice-arrivees" element={<CityPage />} />
          <Route path="/chauffeur-aeroport-nice" element={<CityPage />} />
          <Route path="/transfert-aeroport-nice" element={<CityPage />} />
          <Route path="/vtc-cannes" element={<CityPage />} />
          <Route path="/vtc-monaco" element={<CityPage />} />
          <Route path="/vtc-antibes" element={<CityPage />} />
          <Route path="/vtc-cote-dazur" element={<CityPage />} />
          <Route path="/vtc-nice-monaco" element={<CityPage />} />
          <Route path="/vtc-nice-cannes" element={<CityPage />} />
          <Route path="/vtc-nice-saint-tropez" element={<CityPage />} />
          <Route path="/chauffeur-prive-monaco" element={<CityPage />} />

          {/* Common pages */}
          <Route path="/tarifs" element={<Tarifs />} />
          <Route path="/mon-parcours" element={<MonParcours />} />
          <Route path="/galerie" element={<Galerie />} />
          <Route path="/partenaires-hotels" element={<PartenairesHotels />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Public quote acceptance (no login — token-secured) */}
          <Route path="/devis/:token" element={<PublicQuote />} />

          {/* SumUp payment result (no login) */}
          <Route path="/payment/result" element={<PaymentResult />} />
          <Route path="/pourboire" element={<TipPage />} />

          {/* Protected pages */}
          <Route path="/mon-compte" element={<ProtectedRoute><MonCompte /></ProtectedRoute>} />
          <Route path="/chauffeur" element={<ProtectedRoute roles={["driver", "admin"]}><Chauffeur /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><Admin /></ProtectedRoute>} />

          {/* Legal */}
          <Route path="/mentions-legales" element={<Legal />} />
          <Route path="/confidentialite" element={<Legal />} />
          <Route path="/cgv" element={<Legal />} />

          {/* Language home aliases */}
          <Route path="/en" element={<Home />} />
          <Route path="/it" element={<Home />} />
          <Route path="/ro" element={<Home />} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ConsentProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <CookieConsentBanner />
          </BrowserRouter>
        </AuthProvider>
      </ConsentProvider>
    </HelmetProvider>
  );
}

export default App;
